<template>
  <form @submit.prevent="save">
    <modal-card :title="'Coupon'">
      <loading v-if="loading" />

      <div v-else>
        <b-field>
          <b-switch
            v-model="isGeneratedCode"
            :disabled="!isNew || processing"
            @input="onManualCodeChange"
          >
            Auto-generate coupon code
          </b-switch>
        </b-field>

        <!-- Keys stop Vue reusing the inputs across the v-if branches when
        the auto-generate switch is toggled -->
        <b-field
          v-if="!isNew || !isGeneratedCode"
          key="code"
          label="Code *"
        >
          <b-input
            v-model="internalCouponId"
            required
            placeholder="Enter coupon code"
            :disabled="!isNew || isGeneratedCode || processing"
          />
        </b-field>

        <template v-if="isNew && isGeneratedCode">
          <b-field
            key="quantity"
            label="Quantity *"
            message="How many unique coupons to generate"
          >
            <b-input
              v-model.number="quantity"
              type="number"
              placeholder="1"
              :disabled="processing"
              min="1"
              max="1000"
              required
            />
          </b-field>

          <b-field
            key="format"
            label="Code format"
            :type="formatError ? 'is-danger' : ''"
            :message="formatError || formatHelp"
          >
            <b-input
              v-model="format"
              placeholder="eg. FXD- or FXD-[A-Z0-9]{3}-[0-9]{3}-[A-Z]{3}"
              :disabled="processing"
            />
          </b-field>
        </template>

        <b-field>
          <b-switch
            v-model="expires"
            :disabled="processing"
            @input="onExpiresChanged"
          >
            Auto-expire coupon
          </b-switch>
        </b-field>

        <b-field
          v-if="expires"
          label="Expiry date *"
        >
          <schedule-picker
            v-model="form.dateExpires"
            allow-only-date
            :disabled="processing"
          />
        </b-field>

        <b-field label="Use limit *">
          <b-input
            v-model.number="form.useLimit"
            type="number"
            placeholder="Enter use limit"
            :disabled="processing"
            min="1"
          />
        </b-field>

        <b-field
          v-if="!isNew"
          label="Use count"
        >
          <b-input
            v-model.number="form.useCount"
            type="number"
            placeholder="Enter use count"
            disabled
            min="0"
          />
        </b-field>
      </div>

      <button
        slot="footer"
        :class="{ 'is-loading': processing }"
        :disabled="!isValid || loading || processing || !hasChange"
        type="submit"
        class="button is-success"
      >
        {{
          isNew && isGeneratedCode && quantity > 1
            ? `Generate ${quantity} coupons`
            : "Save"
        }}
      </button>
    </modal-card>
  </form>
</template>

<script>
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  deleteField,
  setDoc,
  updateDoc,
  writeBatch
} from "@firebase/firestore";

const MAX_QUANTITY = 1000;
const BATCH_SIZE = 400; // Firestore caps batched writes at 500
const READ_CHUNK = 50; // parallel existence checks per round

/* Code-format syntax: literal characters plus [CLASS]{n} groups, where CLASS
supports ranges and individual characters, eg. FXD-[A-Z0-9]{3}-[0-9]{3}.
A format with no groups is treated as a prefix for a Firestore auto-ID. */
const TOKEN_RE = /\[([^\]]+)\]\{(\d+)\}/g;

const expandClass = source => {
  let chars = "";
  for (let i = 0; i < source.length; i++) {
    if (source[i + 1] === "-" && source[i + 2] !== undefined) {
      for (
        let code = source.charCodeAt(i);
        code <= source.charCodeAt(i + 2);
        code++
      ) {
        chars += String.fromCharCode(code);
      }
      i += 2;
    } else {
      chars += source[i];
    }
  }
  return [...new Set(chars)].join("");
};

const parseFormat = format => {
  const segments = [];
  let last = 0;
  let match;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(format))) {
    if (match.index > last)
      segments.push({ literal: format.slice(last, match.index) });
    segments.push({
      chars: expandClass(match[1]),
      count: parseInt(match[2], 10)
    });
    last = TOKEN_RE.lastIndex;
  }
  if (last < format.length) segments.push({ literal: format.slice(last) });
  return segments;
};

const randomIndex = max => {
  const buffer = new Uint32Array(1);
  window.crypto.getRandomValues(buffer);
  return buffer[0] % max;
};
export default {
  name: "CouponModal",
  props: {
    couponId: {
      type: String,
      required: false,
      default: ""
    },
    promotionId: {
      type: String,
      required: false,
      default: ""
    }
  },
  data() {
    return {
      isGeneratedCode: true,
      internalCouponId:
        this.couponId || doc(collection(this.$firestore, `coupons`)).id,
      quantity: 1,
      format: "FXD-[A-Z0-9]{3}-[A-Z0-9]{6}",
      loading: false,
      processing: false,
      fields: ["useLimit", "dateExpires", "useLimitPerUser"],
      coupon: {},
      expires: false,
      form: {
        dateCreated: null,
        promotionRef: null,
        //dateExpires: null,
        useCount: 0,
        useLimit: 1
      }
    };
  },
  computed: {
    userId() {
      return this.$store.getters["auth/userId"]();
    },
    isValid() {
      if (this.expires && !this.$_.isDate(this.form.dateExpires)) return false;
      if (this.$_.isEmpty(this.promotionId)) return false;
      if (this.isNew && this.isGeneratedCode) {
        if (!this.$_.isInteger(this.quantity)) return false;
        if (this.quantity < 1 || this.quantity > MAX_QUANTITY) return false;
        if (this.formatError) return false;
      } else if (this.$_.isEmpty(this.internalCouponId)) return false;
      return true;
    },
    /* Doc IDs can't contain '/', and whitespace in codes only causes grief */
    sanitisedFormat() {
      return this.$_.trim(this.format).replace(/[/\s]/g, "");
    },
    hasFormatTokens() {
      return /\[[^\]]+\]\{\d+\}/.test(this.sanitisedFormat);
    },
    formatSegments() {
      return parseFormat(this.sanitisedFormat);
    },
    formatCombinations() {
      return this.formatSegments.reduce(
        (total, segment) =>
          segment.chars !== undefined
            ? total * Math.pow(segment.chars.length, segment.count)
            : total,
        1
      );
    },
    formatError() {
      if (!this.hasFormatTokens) return ""; // plain prefix – nothing to check
      for (const segment of this.formatSegments) {
        if (segment.chars === undefined) continue;
        if (!segment.chars.length)
          return "Invalid character class in code format";
        if (segment.count < 1 || segment.count > 50)
          return "Group lengths must be between 1 and 50";
      }
      if (this.formatCombinations < this.quantity * 2)
        return "This format doesn't have enough unique combinations for that quantity";
      return "";
    },
    formatPreview() {
      if (!this.hasFormatTokens || this.formatError) return "";
      return this.randomCode();
    },
    formatHelp() {
      if (this.formatPreview) return `Optional – eg. ${this.formatPreview}`;
      return "Optional – a plain value is used as a prefix (eg. fxd-); use [A-Z0-9]{3} style groups for a custom pattern";
    },
    isNew() {
      return this.$_.isEmpty(this.couponId);
    },
    hasChange() {
      return !this.$_.isEqual(
        this.$_.pick(this.form, this.fields),
        this.$_.pick(this.coupon, this.fields)
      );
    }
  },
  created() {
    this.getCoupon();
  },
  methods: {
    onManualCodeChange(isGeneratedCode) {
      if (!isGeneratedCode) this.$set(this, "internalCouponId", "");
      else
        this.$set(
          this,
          "internalCouponId",
          doc(collection(this.$firestore, `coupons`)).id
        );
    },
    onExpiresChanged(expires) {
      if (expires) this.$set(this.form, "dateExpires", null);
      else this.$delete(this.form, "dateExpires");
    },
    getCoupon() {
      this.loading = true;
      let ref = doc(this.$firestore, "coupons", this.internalCouponId);
      getDoc(ref)
        .then(coupon => {
          this.loading = false;
          if (coupon.exists()) {
            this.coupon = this.$_.merge({}, { _id: coupon.id }, coupon.data());
            this.form = this.$_.merge({}, { _id: coupon.id }, coupon.data());
            if (coupon.data().dateExpires) {
              this.expires = true;
              this.form.dateExpires = coupon.data().dateExpires.toDate();
              this.coupon.dateExpires = coupon.data().dateExpires.toDate();
            }
          }
        })
        .catch(error => {
          this.loading = false;
          this.$toast.open({
            message: error.message,
            type: "is-danger"
          });
        });
    },
    /* Build a single random code from the parsed format segments */
    randomCode() {
      return this.formatSegments
        .map(segment =>
          segment.literal !== undefined
            ? segment.literal
            : this.$_.times(
                segment.count,
                () => segment.chars[randomIndex(segment.chars.length)]
              ).join("")
        )
        .join("");
    },
    /* Produce `count` unique, unclaimed codes. Prefix-only formats ride on
    Firestore auto-IDs (collision-proof); token formats are generated locally
    then verified against existing coupons, topping up on any clash. */
    async generateUniqueCodes(count) {
      if (!this.hasFormatTokens) {
        return this.$_.times(
          count,
          () =>
            `${this.sanitisedFormat}${
              doc(collection(this.$firestore, `coupons`)).id
            }`
        );
      }
      const seen = new Set();
      let attempts = 0;
      const maxAttempts = count * 50;
      const draw = () => {
        while (attempts++ < maxAttempts) {
          const candidate = this.randomCode();
          if (!seen.has(candidate)) {
            seen.add(candidate);
            return candidate;
          }
        }
        throw new Error(
          `Couldn't generate ${count} unique codes with this format – try a longer pattern`
        );
      };
      let pending = this.$_.times(count, draw);
      const confirmed = [];
      while (pending.length) {
        const chunk = pending.splice(0, READ_CHUNK);
        const snapshots = await Promise.all(
          chunk.map(code => getDoc(doc(this.$firestore, `coupons`, code)))
        );
        snapshots.forEach((snapshot, index) => {
          if (snapshot.exists()) pending.push(draw()); // already claimed
          else confirmed.push(chunk[index]);
        });
      }
      return confirmed;
    },
    /* Generate one or more coupons with unique codes, written in batches */
    async generateCoupons() {
      this.processing = true;
      const template = {
        dateCreated: serverTimestamp(),
        promotionRef: doc(this.$firestore, `promotions/${this.promotionId}`),
        useCount: 0,
        useLimit: this.form.useLimit
      };
      if (this.expires) template.dateExpires = this.form.dateExpires;
      try {
        const codes = await this.generateUniqueCodes(this.quantity);
        for (let i = 0; i < codes.length; i += BATCH_SIZE) {
          const batch = writeBatch(this.$firestore);
          codes.slice(i, i + BATCH_SIZE).forEach(code => {
            batch.set(doc(this.$firestore, `coupons`, code), template);
          });
          await batch.commit();
        }
        this.processing = false;
        this.$emit("success");
        this.$toast.open({
          message:
            this.quantity === 1
              ? `Coupon saved`
              : `${this.quantity} coupons generated`
        });
      } catch (error) {
        this.processing = false;
        this.$toast.open({
          message: error.message || `Error generating coupons`,
          type: "is-danger"
        });
      }
    },
    async save() {
      if (!this.isValid) return;

      if (this.isNew && this.isGeneratedCode) return this.generateCoupons();

      this.processing = true;
      let form = this.form;
      const couponsRef = doc(this.$firestore, `coupons`, this.internalCouponId);
      if (this.isNew) {
        const existingCoupon = await getDoc(couponsRef);
        if (existingCoupon.exists()) {
          this.processing = false;
          return this.$toast.open({
            message: `Coupon with code ${this.internalCouponId} exists`,
            position: "is-bottom",
            queue: false,
            type: "is-danger"
          });
        }

        form.dateCreated = serverTimestamp();
        form.promotionRef = doc(
          this.$firestore,
          `promotions/${this.promotionId}`
        );
      } else {
        form = this.$_.pick(form, ["useLimit", "dateExpires"]);
      }

      if (!this.expires && !this.isNew) form.dateExpires = deleteField();

      (this.isNew ? setDoc(couponsRef, form) : updateDoc(couponsRef, form))
        .then(() => {
          this.processing = false;
          this.$emit("success");
          this.$toast.open({
            message: `Coupon saved`
          });
        })
        .catch(() => {
          this.processing = false;
          this.$toast.open({
            message: `Error saving coupon`,
            type: "is-danger"
          });
        });
    }
  }
};
</script>
